import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Plus,
  ChevronDown,
  ChevronUp,
  MapPin,
  Phone,
  User,
  Pencil,
  Trash2,
  Sparkles,
} from 'lucide-react';
import { PageHeader } from '@/components/PageHeader';
import {
  Page,
  Main,
  Card,
  Button,
  Modal,
  Field,
  Spinner,
  LoadingState,
  ErrorState,
  EmptyState,
  inputClass,
} from '@/components/ui';
import { PickerSheet, PickerRow } from '@/features/wardrobe/components/PickerSheet';
import { useApi, useMutation } from '@/hooks/useApi';
import { SERVICE_PROVIDER_TYPES } from '@/lib/constants';
import { formatCurrency, formatDate, cn } from '@/lib/utils';
import api from '@/lib/api';
import type { ServiceProvider, ServiceRecord } from '@/lib/types';

interface ProviderForm {
  name: string;
  providerType: string;
  address: string;
  phone: string;
  contactPerson: string;
  notes: string;
}

const EMPTY_FORM: ProviderForm = {
  name: '',
  providerType: '',
  address: '',
  phone: '',
  contactPerson: '',
  notes: '',
};

function typeLabel(id: string): string {
  return SERVICE_PROVIDER_TYPES.find((t) => t.id === id)?.label ?? 'Other';
}

export function ServicesPage() {
  const { data, loading, error, refetch } = useApi<{ serviceProviders: ServiceProvider[] }>('/service-providers');
  const { mutate, loading: saving } = useMutation();

  const providers = data?.serviceProviders ?? [];
  const totalItemsOut = providers.reduce((sum, p) => sum + (p.itemsCurrently ?? 0), 0);

  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [history, setHistory] = useState<Record<string, ServiceRecord[]>>({});
  const [historyLoading, setHistoryLoading] = useState<string | null>(null);

  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState<ServiceProvider | null>(null);
  const [form, setForm] = useState<ProviderForm>(EMPTY_FORM);
  const [showTypePicker, setShowTypePicker] = useState(false);

  const [deleteConfirm, setDeleteConfirm] = useState<ServiceProvider | null>(null);

  const toggleExpand = async (provider: ServiceProvider) => {
    if (expandedId === provider.id) {
      setExpandedId(null);
      return;
    }
    setExpandedId(provider.id);
    if (!history[provider.id]) {
      setHistoryLoading(provider.id);
      try {
        const res = await api.get<{ serviceProvider: ServiceProvider }>(`/service-providers/${provider.id}`);
        setHistory((prev) => ({ ...prev, [provider.id]: res.serviceProvider.serviceHistory ?? [] }));
      } catch {
        setHistory((prev) => ({ ...prev, [provider.id]: [] }));
      } finally {
        setHistoryLoading(null);
      }
    }
  };

  const openAdd = () => {
    setEditing(null);
    setForm(EMPTY_FORM);
    setShowModal(true);
  };

  const openEdit = (provider: ServiceProvider) => {
    setEditing(provider);
    setForm({
      name: provider.name,
      providerType: provider.providerType,
      address: provider.address ?? '',
      phone: provider.phone ?? '',
      contactPerson: provider.contactPerson ?? '',
      notes: provider.notes ?? '',
    });
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!form.name.trim() || !form.providerType) return;
    const body = {
      name: form.name.trim(),
      providerType: form.providerType,
      address: form.address.trim() || undefined,
      phone: form.phone.trim() || undefined,
      contactPerson: form.contactPerson.trim() || undefined,
      notes: form.notes.trim() || undefined,
    };
    const res = editing
      ? await mutate('put', `/service-providers/${editing.id}`, body)
      : await mutate('post', '/service-providers', body);
    if (res !== null) {
      setShowModal(false);
      refetch();
    }
  };

  const handleDelete = async () => {
    if (!deleteConfirm) return;
    const res = await mutate('delete', `/service-providers/${deleteConfirm.id}`);
    if (res !== null) {
      setDeleteConfirm(null);
      setExpandedId(null);
      refetch();
    }
  };

  return (
    <Page>
      <PageHeader section="Service Providers" backHref="/profile" backLabel="Profile" />

      <Main className="space-y-5">
        {loading && <LoadingState />}
        {error && <ErrorState message={error} />}

        {!loading && !error && (
          <>
            <Card className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Service Providers</p>
                  <p className="text-2xl font-bold text-foreground">{providers.length}</p>
                </div>
                <div className="text-right">
                  <p className="text-sm text-muted-foreground">Items Out</p>
                  <p className="text-xl font-semibold text-foreground">{totalItemsOut}</p>
                </div>
              </div>
            </Card>

            <Button fullWidth onClick={openAdd}>
              <Plus className="w-5 h-5" />
              Add Service Provider
            </Button>

            {providers.length === 0 ? (
              <EmptyState
                icon={<Sparkles className="w-8 h-8" />}
                title="No service providers yet"
                description="Add cleaners, tailors, cobblers, and more."
              />
            ) : (
              <div className="space-y-3">
                {providers.map((provider) => {
                  const expanded = expandedId === provider.id;
                  const records = history[provider.id] ?? [];
                  return (
                    <Card key={provider.id} className="overflow-hidden">
                      <button
                        onClick={() => toggleExpand(provider)}
                        className="w-full flex items-center gap-4 p-4 text-left"
                      >
                        <div className="p-3 bg-secondary rounded-xl">
                          <Sparkles className="w-5 h-5 text-muted-foreground" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className="text-sm font-semibold text-foreground">{provider.name}</h3>
                          <p className="text-xs text-muted-foreground">{typeLabel(provider.providerType)}</p>
                        </div>
                        {(provider.itemsCurrently ?? 0) > 0 && (
                          <span className="px-2 py-1 bg-accent/20 text-accent-foreground rounded-full text-xs font-medium">
                            {provider.itemsCurrently} item{provider.itemsCurrently !== 1 ? 's' : ''}
                          </span>
                        )}
                        {expanded ? (
                          <ChevronUp className="w-5 h-5 text-muted-foreground" />
                        ) : (
                          <ChevronDown className="w-5 h-5 text-muted-foreground" />
                        )}
                      </button>

                      {expanded && (
                        <div className="px-4 pb-4 space-y-4">
                          <div className="border-t border-border pt-4 space-y-3">
                            {provider.address && (
                              <div className="flex items-start gap-3">
                                <MapPin className="w-4 h-4 text-muted-foreground mt-0.5" />
                                <div>
                                  <p className="text-xs text-muted-foreground">Address</p>
                                  <p className="text-sm text-foreground">{provider.address}</p>
                                </div>
                              </div>
                            )}
                            {provider.phone && (
                              <div className="flex items-start gap-3">
                                <Phone className="w-4 h-4 text-muted-foreground mt-0.5" />
                                <div>
                                  <p className="text-xs text-muted-foreground">Phone</p>
                                  <a href={`tel:${provider.phone}`} className="text-sm text-primary">
                                    {provider.phone}
                                  </a>
                                </div>
                              </div>
                            )}
                            {provider.contactPerson && (
                              <div className="flex items-start gap-3">
                                <User className="w-4 h-4 text-muted-foreground mt-0.5" />
                                <div>
                                  <p className="text-xs text-muted-foreground">Contact Person</p>
                                  <p className="text-sm text-foreground">{provider.contactPerson}</p>
                                </div>
                              </div>
                            )}
                            {provider.notes && (
                              <div className="p-3 bg-secondary/50 rounded-xl">
                                <p className="text-xs text-muted-foreground mb-1">Notes</p>
                                <p className="text-sm text-foreground">{provider.notes}</p>
                              </div>
                            )}

                            <div className="grid grid-cols-2 gap-3">
                              <div className="bg-secondary/50 rounded-xl p-3 text-center">
                                <p className="text-lg font-bold text-foreground">
                                  {formatCurrency(provider.totalSpend ?? 0)}
                                </p>
                                <p className="text-xs text-muted-foreground">Total Spend</p>
                              </div>
                              <div className="bg-secondary/50 rounded-xl p-3 text-center">
                                <p className="text-lg font-bold text-foreground">{provider.serviceCount ?? 0}</p>
                                <p className="text-xs text-muted-foreground">Services</p>
                              </div>
                            </div>

                            {/* Service History */}
                            <div className="space-y-2">
                              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                                Service History
                              </p>
                              {historyLoading === provider.id ? (
                                <div className="flex justify-center py-3">
                                  <Spinner />
                                </div>
                              ) : records.length === 0 ? (
                                <p className="text-sm text-muted-foreground text-center py-2">No service history</p>
                              ) : (
                                <div className="bg-secondary/30 rounded-xl overflow-hidden divide-y divide-border">
                                  {records.slice(0, 8).map((record) => (
                                    <Link
                                      key={record.id}
                                      to={`/wardrobe/item/${record.itemId}`}
                                      className="flex items-center justify-between px-3 py-2.5 hover:bg-secondary/50 transition-colors"
                                    >
                                      <div className="min-w-0">
                                        <p className="text-sm font-medium text-foreground truncate">
                                          {record.providerName}
                                        </p>
                                        <p className="text-xs text-muted-foreground">
                                          {record.serviceType} · {formatDate(record.dropOffDate)}
                                        </p>
                                      </div>
                                      <div className="flex items-center gap-2 flex-shrink-0">
                                        {record.cost != null && (
                                          <span className="text-sm text-foreground">
                                            {formatCurrency(record.cost)}
                                          </span>
                                        )}
                                        <span
                                          className={cn(
                                            'px-2 py-0.5 rounded-full text-[10px] font-medium',
                                            record.status === 'completed'
                                              ? 'bg-green-100 text-green-700'
                                              : 'bg-amber-100 text-amber-700',
                                          )}
                                        >
                                          {record.status === 'completed' ? 'Done' : 'In Progress'}
                                        </span>
                                      </div>
                                    </Link>
                                  ))}
                                </div>
                              )}
                            </div>
                          </div>

                          <div className="flex gap-2 pt-2">
                            <button
                              onClick={() => openEdit(provider)}
                              className="flex-1 flex items-center justify-center gap-2 py-2.5 bg-secondary rounded-xl text-sm font-medium text-foreground hover:bg-secondary/70 transition-colors"
                            >
                              <Pencil className="w-4 h-4" />
                              Edit
                            </button>
                            <button
                              onClick={() => setDeleteConfirm(provider)}
                              className="flex items-center justify-center px-4 py-2.5 bg-destructive/10 rounded-xl text-sm font-medium text-destructive hover:bg-destructive/20 transition-colors"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      )}
                    </Card>
                  );
                })}
              </div>
            )}
          </>
        )}
      </Main>

      {/* Add/Edit Modal */}
      <Modal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        title={editing ? 'Edit Provider' : 'Add Service Provider'}
        footer={
          <Button fullWidth onClick={handleSave} disabled={!form.name.trim() || !form.providerType || saving}>
            {saving ? 'Saving…' : editing ? 'Save Changes' : 'Add Provider'}
          </Button>
        }
      >
        <div className="space-y-4">
          <Field label="Name *">
            <input
              type="text"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="e.g., Jeeves of Belgravia"
              className={inputClass}
            />
          </Field>
          <Field label="Type of Service *">
            <button
              type="button"
              onClick={() => setShowTypePicker(true)}
              className={cn(inputClass, 'flex items-center justify-between')}
            >
              <span className={form.providerType ? 'text-foreground' : 'text-muted-foreground'}>
                {form.providerType ? typeLabel(form.providerType) : 'Select service type'}
              </span>
              <ChevronDown className="w-4 h-4 text-muted-foreground" />
            </button>
          </Field>
          <Field label="Address">
            <input
              type="text"
              value={form.address}
              onChange={(e) => setForm({ ...form, address: e.target.value })}
              placeholder="Street address"
              className={inputClass}
            />
          </Field>
          <Field label="Phone">
            <input
              type="tel"
              value={form.phone}
              onChange={(e) => setForm({ ...form, phone: e.target.value })}
              placeholder="+1 (555) 000-0000"
              className={inputClass}
            />
          </Field>
          <Field label="Contact Person">
            <input
              type="text"
              value={form.contactPerson}
              onChange={(e) => setForm({ ...form, contactPerson: e.target.value })}
              placeholder="Name of your contact"
              className={inputClass}
            />
          </Field>
          <Field label="Notes">
            <textarea
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              placeholder="Turnaround time, specialties…"
              rows={3}
              className={cn(inputClass, 'resize-none')}
            />
          </Field>
        </div>
      </Modal>

      {/* Type Picker */}
      <PickerSheet isOpen={showTypePicker} onClose={() => setShowTypePicker(false)} title="Service Type">
        <div className="space-y-1">
          {SERVICE_PROVIDER_TYPES.map((type) => (
            <PickerRow
              key={type.id}
              label={type.label}
              selected={form.providerType === type.id}
              onClick={() => {
                setForm((f) => ({ ...f, providerType: type.id }));
                setShowTypePicker(false);
              }}
            />
          ))}
        </div>
      </PickerSheet>

      {/* Delete Confirm */}
      <Modal
        isOpen={deleteConfirm !== null}
        onClose={() => setDeleteConfirm(null)}
        title="Delete Provider?"
        footer={
          <div className="flex gap-3">
            <Button variant="outline" fullWidth onClick={() => setDeleteConfirm(null)} disabled={saving}>
              Cancel
            </Button>
            <Button variant="destructive" fullWidth onClick={handleDelete} disabled={saving}>
              {saving ? 'Deleting…' : 'Delete'}
            </Button>
          </div>
        }
      >
        <p className="text-sm text-muted-foreground">
          Delete {deleteConfirm?.name}?
          {(deleteConfirm?.itemsCurrently ?? 0) > 0 &&
            ` ${deleteConfirm?.itemsCurrently} item(s) are currently at this provider.`}
        </p>
      </Modal>
    </Page>
  );
}
